import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS } from "../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../components/SuccessPage';

const CasinoTaxUploader = () => {
  // Function to get default month and year
  const getDefaultMonthYear = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-11 (January is 0)
    const currentYear = currentDate.getFullYear();

    let defaultMonth, defaultYear;

    if (currentMonth === 0) { // January
      defaultMonth = 'December';
      defaultYear = (currentYear - 1).toString();
    } else {
      // Get previous month
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      defaultMonth = monthNames[currentMonth - 1];
      defaultYear = currentYear.toString();
    }

    return { defaultMonth, defaultYear };
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const fileInputRef = useRef(null);

  // Signature-related state (matching UnstampedPacks format)
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());
  const [printedName, setPrintedName] = useState('');
  const [title, setTitle] = useState('');

  // Retailer Information State
  const [taxpayerId, setTaxpayerId] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [formData, setFormData] = useState({
    distributor: '',
    month: getDefaultMonthYear().defaultMonth,
    year: getDefaultMonthYear().defaultYear,
    expirationDate: '5/23/2025',
    reportMonth: getDefaultMonthYear().defaultMonth,
    reportYear: getDefaultMonthYear().defaultYear
  });

  // Month and Year options
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - 3 + i).toString());

  // Format month for API (convert to number with leading zero)
  const formatMonthForAPI = (monthName) => {
    const monthMap = {
      'January': '01', 'February': '02', 'March': '03', 'April': '04',
      'May': '05', 'June': '06', 'July': '07', 'August': '08',
      'September': '09', 'October': '10', 'November': '11', 'December': '12'
    };
    return monthMap[monthName] || '01';
  };

  // Get taxpayer ID and business name from localStorage on component mount
  useEffect(() => {
    const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
    const storedBusinessName = localStorage.getItem("selectedBusinessName");

    if (storedTaxpayerId) {
      setTaxpayerId(storedTaxpayerId);
      console.log('Retrieved taxpayer ID from localStorage:', storedTaxpayerId);
    } else {
      console.warn('No taxpayer ID found in localStorage');
      setTaxpayerId('109');
    }

    if (storedBusinessName) {
      setBusinessName(storedBusinessName);
      setFormData(prev => ({
        ...prev,
        distributor: storedBusinessName
      }));
      console.log('Retrieved business name from localStorage:', storedBusinessName);
    } else {
      console.warn('No business name found in localStorage');
      setFormData(prev => ({
        ...prev,
        distributor: 'Black Canyon Distribution, LLC - PBPN License #41112-7777'
      }));
    }
  }, []);

  // Sync reportMonth and reportYear when month or year changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      reportMonth: prev.month,
      reportYear: prev.year
    }));
  }, [formData.month, formData.year]);

  // Initialize canvas size after component mounts (matching UnstampedPacks)
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container-casino');
      if (container) {
        const width = container.clientWidth - 32; // Account for padding
        const height = 200;
        setCanvasSize({ width, height });

        // Force canvas redraw with new dimensions
        if (sigCanvas.current) {
          const canvas = sigCanvas.current.getCanvas();
          canvas.width = width;
          canvas.height = height;
        }
      }
    };

    // Initial size calculation
    updateCanvasSize();

    // Add resize listener
    window.addEventListener('resize', updateCanvasSize);

    // Cleanup
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Update canvas size when signature section becomes visible
  useEffect(() => {
    if (showSignature) {
      const timer = setTimeout(() => {
        const container = document.getElementById('signature-container-casino');
        if (container) {
          const width = container.clientWidth - 32;
          const height = 200;
          setCanvasSize({ width, height });

          // Re-initialize canvas with proper dimensions
          if (sigCanvas.current) {
            const canvas = sigCanvas.current.getCanvas();
            canvas.width = width;
            canvas.height = height;
            sigCanvas.current.clear(); // Clear any previous content
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [showSignature]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validExtensions = [
        '.xlsx', '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];

      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isValidFile = validExtensions.includes(`.${fileExtension}`) ||
        validExtensions.includes(file.type);

      if (isValidFile) {
        setSelectedFile(file);
        setUploadStatus('');
        setShowSignature(true);
      } else {
        setUploadStatus('Please select a valid Excel file (.xlsx, .xls)');
        setSelectedFile(null);
        setShowSignature(false);
      }
    }
  };

  // Signature functions (matching UnstampedPacks format)
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    // Signature completion logic - just update state
    setIsSigned(true);
  };

  // Helper function to convert base64 data URL to File object
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  // Handle file upload with API integration
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('Please select a file first');
      return;
    }

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      setUploadStatus('Please provide your signature before submitting');
      return;
    }

    if (!printedName.trim()) {
      setUploadStatus('Please enter your printed name');
      return;
    }

    if (!title.trim()) {
      setUploadStatus('Please enter your title');
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setUploadStatus('');

    try {
      // Convert signature to file and store for PDF generation
      const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(signatureData);
      const signatureFile = dataURLtoFile(signatureData, 'signature.png');

      // Format month for API
      const formattedMonth = formatMonthForAPI(formData.month);
      const dateParam = `${formattedMonth}-${formData.year}`;

      // Construct API URL with query parameters
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&reporttype=casino&date=${dateParam}&test=true`;

      console.log('Submitting to API:', apiUrl);

      // Create FormData object
      const formDataToSend = new FormData();

      // Add Excel file to 'Body' key
      formDataToSend.append('Body', selectedFile);

      // Add signature file to 'Signature' key
      formDataToSend.append('Signature', signatureFile, 'signature.png');

      // Add printed name and title
      formDataToSend.append('printedName', printedName);
      formDataToSend.append('title', title);
      formDataToSend.append('dateSigned', currentDate);

      // Log form data for debugging
      console.log('FormData contents:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(key, value);
      }

      // Submit to API
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend
      });

      // First, get the response as text to handle both JSON and plain text responses
      const responseText = await response.text();
      console.log('API Response:', responseText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, message: ${responseText}`);
      }

       // Try to parse as JSON
    let result;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.warn('Response is not valid JSON, treating as plain text:', responseText);
      // If it's not JSON, treat it as a plain text response
      if (responseText.trim().toLowerCase() === 'true' || responseText.trim().toLowerCase().includes('success')) {
        setUploadStatus('success');
        setProgress(100);
        alert('Sales Tax data submitted successfully!');
        // Reset form after successful upload
        setTimeout(() => {
          handleRemoveFile();
        }, 2000);
        return;
      } else {
        throw new Error(`Unexpected response from server: ${responseText}`);
      }
    }

    // Handle the JSON response based on the provided response formats
    console.log('Parsed JSON result:', result);

    // Case 1: Success with Status field
    if (result && result.Status === 'Success') {
      setUploadStatus('success');
      setProgress(100);
      
      // Store submission data for success page
      setSubmissionData({
        formData,
        printedName,
        title,
        signatureData: signatureDataURL,
        taxpayerId,
        businessName
      });
      
      // Show success screen
      setShowSuccessScreen(true);
      
    } 
    // Case 2: Error with Status field and Message
    else if (result && result.Status === 'Error') {
      setUploadStatus('error');
      setProgress(0);
      
      const errorMessage = result.Message || 'Failed to submit Sales Tax data. Please try again.';
      
      // Special handling for duplicate submission error
      if (result.Message && result.Message.includes('already been submitted')) {
        setUploadStatus(`${result.Message}`);
        alert(`${result.Message}`);
      } else {
        setUploadStatus(errorMessage);
        alert(errorMessage);
      }
      
    }
    // Case 3: Success with different format (backward compatibility)
    else if (result && (result.status === 'success' || result.success === true)) {
      setUploadStatus('success');
      setProgress(100);
      
      // Store submission data for success page
      setSubmissionData({
        formData,
        printedName,
        title,
        signatureData: signatureDataURL,
        taxpayerId,
        businessName
      });
      
      // Show success screen
      setShowSuccessScreen(true);
      
    }
    // Case 4: Error with different format (backward compatibility)
    else if (result && (result.status === 'error' || result.success === false)) {
      setUploadStatus('error');
      setProgress(0);
      
      const errorMessage = result.message || 'Failed to submit Sales Tax data. Please try again.';
      setUploadStatus(errorMessage);
      alert(errorMessage);
      
    }
    // Case 5: Empty success object (like {"Status": "Success"})
    else if (result && Object.keys(result).length === 1 && result.Status) {
      if (result.Status === 'Success') {
        setUploadStatus('success');
        setProgress(100);
        
        // Store submission data for success page
        setSubmissionData({
          formData,
          printedName,
          title,
          signatureData: signatureDataURL,
          taxpayerId,
          businessName
        });
        
        // Show success screen
        setShowSuccessScreen(true);
      } else if (result.Status === 'Error') {
        setUploadStatus('error');
        setProgress(0);
        alert(result.Message);
      }
    }
    // Case 6: Unexpected response format
    else {
      console.warn('Unexpected response format:', result);
      setUploadStatus('error');
      setProgress(0);
      alert('Unexpected response from server. Please try again or contact support.');
    }

  } catch (error) {
    console.error('Error submitting form:', error);
    setUploadStatus('error');
    setProgress(0);

    // Extract a user-friendly error message
    let errorMessage = 'Failed to submit Sales Tax data. Please try again.';
    if (error.message.includes('HTTP error')) {
      errorMessage = `Server error: ${error.message}`;
    } else if (error.message.includes('Unexpected response')) {
      errorMessage = error.message;
    } else if (error.message.includes('Network Error')) {
      errorMessage = 'Network error: Unable to connect to the server. Please check your connection.';
    } else if (error.message.includes('Failed to fetch')) {
      errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection.';
    }

    setUploadStatus(errorMessage);
    alert(errorMessage);
  } finally {
    setIsUploading(false);
  }
  };

  // Generate PDF function
  const generatePDF = (signatureDataOverride = null) => {
    const pdf = new jsPDF();

    // Set font and colors
    pdf.setFont('helvetica');
    pdf.setTextColor(0, 0, 0);

    // Header Section
    let yPosition = 20;
    pdf.setFontSize(16);
    pdf.text('Casino Sales Tax Report Submission', 105, yPosition, { align: 'center' });
    yPosition += 10;

    pdf.setFontSize(12);
    pdf.text('Prairie Band Potawatomi Tax Commission', 105, yPosition, { align: 'center' });
    yPosition += 6;
    pdf.text('16281 Q Road', 105, yPosition, { align: 'center' });
    yPosition += 6;
    pdf.text('Mayetta, KS 66509', 105, yPosition, { align: 'center' });
    yPosition += 10;

    // Helper function to format date
    const formatDate = (dateString) => {
      if (!dateString) return currentDate;
      const date = new Date(dateString);
      return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
    };

    // Report Information Section
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Report Information:', 20, yPosition);
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.text(`Month of Report: ${formData.month} ${formData.year}`, 20, yPosition);
    yPosition += 6;
    pdf.text(`Taxpayer ID: ${taxpayerId || 'N/A'}`, 20, yPosition);
    yPosition += 6;
    pdf.text(`Business Name: ${businessName || formData.distributor || 'N/A'}`, 20, yPosition);
    yPosition += 6;
    pdf.text(`Submitted Date: ${currentDate}`, 20, yPosition);
    yPosition += 6;
    pdf.text(`Excel File: ${selectedFile ? selectedFile.name : 'N/A'}`, 20, yPosition);
    yPosition += 15;

    // Signature section
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Signature and Certification', 105, yPosition, { align: 'center' });
    yPosition += 10;

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('I declare under penalty of perjury under the PBPN law and order codes that the foregoing is true and correct.', 20, yPosition);
    yPosition += 15;

    // Add signature image if available
    const signatureToUse = signatureDataOverride || signatureDataURL;
    if (signatureToUse) {
      try {
        pdf.text('Signature:', 20, yPosition);
        yPosition += 8;

        const signatureWidth = 80;
        const signatureHeight = 30;
        pdf.addImage(signatureToUse, 'PNG', 20, yPosition, signatureWidth, signatureHeight);
        yPosition += signatureHeight + 15;
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        pdf.text('Signature: Provided during submission', 20, yPosition);
        yPosition += 10;
      }
    } else {
      pdf.text('Signature: Not provided', 20, yPosition);
      yPosition += 10;
    }

    if (printedName) {
      pdf.text(`Printed Name: ${printedName}`, 20, yPosition);
      yPosition += 8;
    }
    if (title) {
      pdf.text(`Title: ${title}`, 20, yPosition);
      yPosition += 8;
    }

    pdf.text(`Date Signed: ${currentDate}`, 20, yPosition);
    yPosition += 15;

    // Certification
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'italic');
    const certText = 'I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the Sales Tax data submitted herein is true and correct to the best of my knowledge.';
    const splitCert = pdf.splitTextToSize(certText, 170);
    pdf.text(splitCert, 20, yPosition);

    return pdf;
  };

  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    if (!submissionData) return;

    const pdf = generatePDF(submissionData.signatureData);
    const fileName = `Casino_Sales_Tax_Report_${formData.month}_${formData.year}_${businessName.replace(/\s+/g, '_')}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
    // Reset form after going back
    handleRemoveFile();
  };

  // Download template
  const handleDownloadStaticTemplate = () => {
    setIsDownloading(true);

    try {
      const link = document.createElement('a');
      link.href = `${process.env.PUBLIC_URL || ''}/templates/Retailer_Sales_Tax_Casino.xlsx`;
      link.download = 'Casino_Sales_Tax_Template.xlsx';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setUploadStatus('Template download started!');

      setTimeout(() => {
        setUploadStatus('');
      }, 3000);
    } catch (error) {
      console.error('Error downloading template:', error);
      setUploadStatus('Failed to download template. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle drag and drop
  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileChange({ target: { files } });
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadStatus('');
    setShowSignature(false);
    setIsSigned(false);
    setPrintedName('');
    setTitle('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  // Prepare report details for SuccessPage
  const reportDetails = {
    'Report Type': 'Casino Sales Tax Report',
    'Taxpayer ID': taxpayerId || 'N/A',
    'Business': businessName || formData.distributor || 'N/A',
    'Period': `${formData.month} ${formData.year}`,
    'Submitted': currentDate,
    'Excel File': selectedFile ? selectedFile.name : 'N/A'
  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="Casino Sales Tax Report Submitted Successfully!"
        subtitle={`Your casino sales tax report for ${formData.month} ${formData.year} has been successfully submitted.`}
        downloadButtonText="Download Sales Tax Report PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• Your casino sales tax data has been successfully uploaded</li>
            <li>• Keep the PDF for your records</li>
            <li>• Contact the Tax Commission staff if you need to make any corrections</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-white">
      {/* Enhanced Header */}
      <div className="text-center mb-8 sm:mb-12">
        <div className="inline-flex items-center justify-center w-12 sm:w-16 h-12 sm:h-16 text-white rounded-full mb-3 sm:mb-4" style={{ backgroundColor: '#840028' }}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 sm:h-8 w-6 sm:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-3" style={{ color: '#840028' }}>
          Casino Sales Tax Portal
        </h1>
        <p className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
          Upload your casino sales tax data securely with digital signature verification
        </p>
      </div>

      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl overflow-hidden border border-gray-100">
        <div className="px-4 sm:px-8 py-6 sm:py-10">

          {/* Retailer Information Section - Updated from Distributor Information */}
          <div className="border border-gray-300 p-4 sm:p-6 mb-6 sm:mb-8 rounded-lg">
            <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 border-b border-[#840028] pb-2 text-[#840028]">
              Retailer Information
            </h2>

            <div className="space-y-3 sm:space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                <label className="font-bold sm:w-24 sm:mr-4 text-[#840028] text-sm sm:text-base">Retailer:</label>
                <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-80 rounded text-sm sm:text-base">
                  {formData.distributor}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0">
                <label className="font-bold sm:w-24 sm:mr-4 text-[#840028] text-sm sm:text-base">Month:</label>
                <select
                  value={formData.month}
                  onChange={(e) => handleInputChange('month', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-32 sm:mr-8 text-sm sm:text-base"
                >
                  {monthOptions.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>

                <label className="font-bold sm:w-12 sm:mr-4 text-[#840028] text-sm sm:text-base">Year:</label>
                <select
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-20 text-sm sm:text-base"
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              {/* Hidden fields for API submission */}
              <div className="hidden">
                <input
                  type="text"
                  value={formData.reportMonth}
                  readOnly
                />
                <input
                  type="text"
                  value={formData.reportYear}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Enhanced Download Template Section */}
          <div className="mb-6 sm:mb-10 p-4 sm:p-6 rounded-xl border" style={{ backgroundColor: '#fdf2f8', borderColor: '#f8bbd9' }}>
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#fce7f3' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 sm:h-6 w-5 sm:w-6" style={{ color: '#840028' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-semibold mb-2" style={{ color: '#7c2d12' }}>
                  Need a template?
                </h3>
                <p className="mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base" style={{ color: '#92400e' }}>
                  Download our standardized casino sales tax report template to ensure your data matches the required format.
                </p>
                <button
                  onClick={handleDownloadStaticTemplate}
                  disabled={isDownloading}
                  className={`inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-medium text-white rounded-lg transition-all duration-200 w-full sm:w-auto justify-center ${isDownloading
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:shadow-lg transform hover:-translate-y-0.5'
                    }`}
                  style={{
                    backgroundColor: isDownloading ? '#9ca3af' : '#840028'
                  }}
                  onMouseEnter={(e) => !isDownloading && (e.target.style.backgroundColor = '#6b0021')}
                  onMouseLeave={(e) => !isDownloading && (e.target.style.backgroundColor = '#840028')}
                >
                  {isDownloading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Download Template
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Enhanced Upload Section */}
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6 flex items-center">
              <span className="rounded-full w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold mr-2 sm:mr-3 text-white" style={{ backgroundColor: '#840028' }}>1</span>
              Upload Sales Tax Data
            </h2>

            {/* Enhanced File Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg sm:rounded-xl p-6 sm:p-10 text-center cursor-pointer transition-all duration-300 ${selectedFile
                ? 'border-emerald-400 bg-emerald-50 shadow-lg scale-[1.02]'
                : 'border-gray-300 hover:shadow-md'
                }`}
              style={{
                borderColor: selectedFile ? '#34d399' : '#d1d5db',
                backgroundColor: selectedFile ? '#ecfdf5' : undefined
              }}
              onMouseEnter={(e) => {
                if (!selectedFile) {
                  e.target.style.borderColor = '#840028';
                  e.target.style.backgroundColor = '#fdf2f8';
                }
              }}
              onMouseLeave={(e) => {
                if (!selectedFile) {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileChange}
                className="hidden"
              />

              <div className="space-y-4 sm:space-y-6">
                <div className="mx-auto h-16 sm:h-20 w-16 sm:w-20 text-gray-400">
                  {selectedFile ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-16 sm:w-20 h-16 sm:h-20 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-16 sm:w-20 h-16 sm:h-20">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                </div>

                {selectedFile ? (
                  <div className="space-y-2">
                    <p className="text-lg sm:text-xl font-semibold text-emerald-700 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 sm:h-6 w-5 sm:w-6 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {selectedFile.name}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 bg-white px-2 sm:px-3 py-1 rounded-full inline-block">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-lg sm:text-xl text-gray-700">
                      <span className="font-semibold" style={{ color: '#840028' }}>
                        Click to upload
                      </span>{' '}
                      or drag and drop
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      Excel spreadsheets only (.xlsx, .xls) • Max file size: 50MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* File Actions */}
            {selectedFile && (
              <div className="mt-4 sm:mt-6 flex justify-between items-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                <button
                  onClick={handleRemoveFile}
                  className="flex items-center px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 sm:h-5 w-4 sm:w-5 mr-1 sm:mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Remove File
                </button>
              </div>
            )}
          </div>

          {/* Updated Signature Section - Matching UnstampedPacks Format */}
          {showSignature && (
            <div className="mb-6 sm:mb-8 transition-all duration-500 ease-in-out">
              <h2 className="text-lg sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6 flex items-center">
                <span className="rounded-full w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold mr-2 sm:mr-3 text-white" style={{ backgroundColor: '#840028' }}>2</span>
                Digital Signature Required
              </h2>

              <div className="border border-gray-300 p-4 sm:p-6 rounded-lg">
                <h3 className="text-lg font-bold mb-3 sm:mb-4 text-center text-gray-800">Signature</h3>

                <div className="mb-4">
                  <label className="font-bold block mb-2 text-gray-700 text-sm sm:text-base">
                    Sign below to certify the accuracy of this report:
                  </label>
                  <div
                    id="signature-container-casino"
                    className="border-2 border-gray-300 rounded-lg p-3 sm:p-4 bg-white"
                  >
                    {canvasSize.width > 0 && (
                      <SignatureCanvas
                        ref={sigCanvas}
                        penColor="#0000FF"
                        backgroundColor="rgb(255, 255, 255)"
                        canvasProps={{
                          width: canvasSize.width,
                          height: canvasSize.height,
                          className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                        }}
                        onBegin={handleSignatureBegin}
                        onEnd={handleSignatureEnd}
                        clearOnResize={false}
                      />
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                  <div className="text-sm text-gray-600 text-center sm:text-left">
                    {isSigned ? (
                      <span className="text-green-600 font-bold">✓ Signature provided</span>
                    ) : (
                      <span className="text-red-600">Please sign above</span>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={clearSignature}
                    className="px-4 py-2 bg-[#840028] text-white rounded hover:bg-[#6a0020] transition duration-200 text-sm w-full sm:w-auto"
                  >
                    Clear Signature
                  </button>
                </div>

                {/* Printed Name and Title Fields - Added */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Printed Name: <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={printedName}
                        onChange={(e) => setPrintedName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title: <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Enter your title"
                        required
                      />
                    </div>
                  </div>

                  {/* Date Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Signed:
                    </label>
                    <input
                      type="text"
                      value={currentDate}
                      readOnly
                      className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This date is automatically set to today's date and cannot be modified
                    </p>
                  </div>
                </div>
              </div>

              {/* Certification */}
              <div className="border-t-2 border-b-2 border-[#840028] py-4 sm:py-6 my-6 sm:my-8 text-center italic text-[#840028] text-sm sm:text-base">
                <p>
                  I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the statements made herein are true and correct to the best of my knowledge.
                </p>
              </div>
            </div>
          )}

          {/* Submit Section */}
          {selectedFile && (
            <div className="mb-6 sm:mb-8">
              <h2 className="text-lg sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6 flex items-center">
                <span className="rounded-full w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center text-xs sm:text-sm font-bold mr-2 sm:mr-3 text-white" style={{ backgroundColor: '#840028' }}>3</span>
                Submit Tax Data
              </h2>

              <div className="flex justify-center">
                <button
                  onClick={handleUpload}
                  disabled={isUploading || !isSigned || !printedName.trim() || !title.trim()}
                  className={`flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium text-white rounded-lg sm:rounded-xl transition-all duration-200 w-full sm:w-auto ${isUploading || !isSigned || !printedName.trim() || !title.trim()
                    ? 'cursor-not-allowed'
                    : 'hover:shadow-lg transform hover:-translate-y-1'
                    }`}
                  style={{
                    backgroundColor: isUploading || !isSigned || !printedName.trim() || !title.trim() ? '#9ca3af' : '#840028'
                  }}
                  onMouseEnter={(e) => {
                    if (!isUploading && isSigned && printedName.trim() && title.trim()) {
                      e.target.style.backgroundColor = '#6b0021';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isUploading && isSigned && printedName.trim() && title.trim()) {
                      e.target.style.backgroundColor = '#840028';
                    }
                  }}
                >
                  {isUploading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 sm:h-5 w-4 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing Submission...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 sm:h-6 w-5 sm:w-6 mr-2 sm:mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      {isSigned && printedName.trim() && title.trim() ? 'Submit Sales Tax Data' : 'Please Complete All Required Fields'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Enhanced Progress Bar */}
          {isUploading && (
            <div className="mb-6 sm:mb-8 rounded-lg sm:rounded-xl p-4 sm:p-6" style={{ backgroundColor: '#fdf2f8' }}>
              <div className="flex justify-between items-center mb-2 sm:mb-3">
                <span className="text-xs sm:text-sm font-medium" style={{ color: '#7c2d12' }}>Uploading sales tax data with signature verification</span>
                <span className="text-xs sm:text-sm font-bold" style={{ color: '#7c2d12' }}>{progress}%</span>
              </div>
              <div className="w-full rounded-full h-2 sm:h-3 overflow-hidden" style={{ backgroundColor: '#f8bbd9' }}>
                <div
                  className="h-2 sm:h-3 rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: `${progress}%`,
                    backgroundColor: '#840028'
                  }}
                ></div>
              </div>
              <p className="text-xs mt-1 sm:mt-2" style={{ color: '#92400e' }}>Please wait while we process your submission...</p>
            </div>
          )}

          {/* Enhanced Status Message */}
          {uploadStatus && (
            <div
              className={`p-4 sm:p-6 rounded-lg sm:rounded-xl border text-center transition-all duration-300 ${uploadStatus.includes('success')
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : uploadStatus.includes('failed') || uploadStatus.includes('Please select') || uploadStatus.includes('Please provide') || uploadStatus.includes('Please enter')
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : ''
                }`}
              style={{
                backgroundColor: uploadStatus.includes('success') ? '#ecfdf5' :
                  uploadStatus.includes('failed') || uploadStatus.includes('Please select') || uploadStatus.includes('Please provide') || uploadStatus.includes('Please enter') ? '#fef2f2' :
                    '#fdf2f8',
                borderColor: uploadStatus.includes('success') ? '#a7f3d0' :
                  uploadStatus.includes('failed') || uploadStatus.includes('Please select') || uploadStatus.includes('Please provide') || uploadStatus.includes('Please enter') ? '#fecaca' :
                    '#f8bbd9',
                color: uploadStatus.includes('success') ? '#047857' :
                  uploadStatus.includes('failed') || uploadStatus.includes('Please select') || uploadStatus.includes('Please provide') || uploadStatus.includes('Please enter') ? '#dc2626' :
                    '#7c2d12'
              }}
            >
              <div className="flex items-center justify-center">
                {uploadStatus.includes('success') ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 sm:h-6 w-5 sm:w-6 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : uploadStatus.includes('failed') || uploadStatus.includes('Please select') || uploadStatus.includes('Please provide') || uploadStatus.includes('Please enter') ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 sm:h-6 w-5 sm:w-6 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 sm:h-6 w-5 sm:w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span className="font-medium text-sm sm:text-base">{uploadStatus}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container-casino {
          min-height: 200px;
        }
        
        @media (max-width: 640px) {
          #signature-container-casino {
            min-height: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default CasinoTaxUploader;